import dayjs from 'utils/dayjs';

import {
  CHART_WATERMARK_IMAGE,
  CHART_WATERMARK_OFFSET,
  getChartWatermarkHeight,
} from 'utils/chartWatermark';
import { periods } from 'constants/currency';
import { colorGrey400 } from 'styles';
import { DEFAULT_MOBILE_DATE_FORMAT } from './defaultValues';

let watermarkImage = null;
let chartLib = null;
let chartLibPromise = null;

const loadChartLib = () => {
  if (!chartLibPromise) {
    chartLibPromise = import(
      /* webpackChunkName: "chartjs" */ './chartLib'
    ).then(module => {
      chartLib = module.default;

      return chartLib;
    });
  }

  return chartLibPromise;
};

const redrawChartsWithWatermark = () => {
  if (!chartLib?.instances) {
    return;
  }

  Object.values(chartLib.instances).forEach(chart => {
    chart.draw();
  });
};

const getWatermarkImage = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  if (!watermarkImage) {
    watermarkImage = new window.Image();
    watermarkImage.src = CHART_WATERMARK_IMAGE;
    watermarkImage.onload = redrawChartsWithWatermark;
    watermarkImage.onerror = () => {};
  }

  return watermarkImage;
};

const chartWatermarkPlugin = {
  id: 'customWatermark',
  afterDraw: chart => {
    const { ctx, chartArea, width } = chart;
    const image = getWatermarkImage();

    if (!chartArea || !image?.complete || !image.naturalWidth) {
      return;
    }

    const logoHeight = getChartWatermarkHeight(width);
    const logoWidth = (image.naturalWidth / image.naturalHeight) * logoHeight;
    const x = chartArea.left + CHART_WATERMARK_OFFSET;
    const y = chartArea.bottom - logoHeight - CHART_WATERMARK_OFFSET;

    ctx.drawImage(image, x, y, logoWidth, logoHeight);
  },
};

export const createChart = async (el, type, labels, datasets, options = {}) => {
  const Chart = await loadChartLib();

  getWatermarkImage();

  const chart = new Chart(el, {
    type,
    data: {
      labels,
      datasets,
    },
    plugins: [chartWatermarkPlugin],
    options: {
      responsive: true,
      ...options,
    },
  });

  return chart;
};

export const getFontSize = context => {
  const { width } = context.chart;
  if (width >= 690) return Math.round(width / 60);
  return Math.round(width / 42);
};

function getGradient(ctx, chartArea, color, colorBlack) {
  let width;
  let height;
  let gradient;
  const chartWidth = chartArea.right - chartArea.left;
  const chartHeight = chartArea.bottom - chartArea.top;
  if (gradient === null || width !== chartWidth || height !== chartHeight) {
    width = chartWidth;
    height = chartHeight;
    gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
    gradient.addColorStop(0, color);
    gradient.addColorStop(1, colorBlack);
  }

  return gradient;
}

export const generateDatasets = (currTerms, data, type) => {
  let dataset = [];

  currTerms.forEach(el => {
    if (el.type === 'sell') {
      dataset = [
        ...dataset,
        {
          label: el.label,
          data: data[el.value]?.sell,
          backgroundColor(context) {
            const { chart } = context;
            const { ctx, chartArea } = chart;

            if (!chartArea) {
              return null;
            }
            return getGradient(
              ctx,
              chartArea,
              'rgba(223, 49, 21, 0.15)',
              'rgba(102, 134, 199, 0)'
            );
          },
          borderColor: ['#E97360'],
          borderWidth: 2,
          borderDash: type !== 'nbu' ? [0, 0] : [7, 7],
          fill: type !== 'nbu',
        },
      ];
    } else if (el.type === 'nbu') {
      dataset = [
        ...dataset,
        {
          label: el.label,
          data: data[el.value]?.rate,
          backgroundColor(context) {
            const { chart } = context;
            const { ctx, chartArea } = chart;

            if (!chartArea) {
              return null;
            }
            return getGradient(
              ctx,
              chartArea,
              'rgba(0, 178, 255, 0.49)',
              'rgba(0, 178, 255, 0)'
            );
          },
          borderColor: ['#00B2FF'],
          borderWidth: 2,
          borderDash: type === 'nbu' ? [0, 0] : [7, 7],
          fill: type === 'nbu',
        },
      ];
    } else if (el.type === 'buy') {
      dataset = [
        ...dataset,
        {
          label: el.label,
          data: data[el.value]?.buy,
          backgroundColor(context) {
            const { chart } = context;
            const { ctx, chartArea } = chart;

            if (!chartArea) {
              return null;
            }
            return getGradient(
              ctx,
              chartArea,
              'rgba(53, 164, 124, 0.24)',
              'rgba(53, 164, 124, 0)'
            );
          },
          borderColor: ['#76C1A6'],
          borderWidth: 2,
          borderDash: type !== 'nbu' ? [0, 0] : [7, 7],
          fill: type !== 'nbu',
        },
      ];
    }
  });
  return dataset;
};

export const generateBarDatasets = currTerms => {
  let dataset = [];
  currTerms.forEach(el => {
    if (el.type === 'sell') {
      dataset = [
        ...dataset,
        {
          label: 'Продажа',
          data: el.data,
          backgroundColor: 'rgba(223, 49, 21, 0.5)',
          hoverBackgroundColor: '#DF3115',
          borderColor: '#DF3115',
          // barThickness: 18,
          categoryPercentage: 0.7,
          barPercentage: 0.8,
        },
      ];
    } else if (el.type === 'buy') {
      dataset = [
        ...dataset,
        {
          label: 'Покупка',
          data: el.data,
          backgroundColor: 'rgba(118, 193, 166, 0.5)',
          hoverBackgroundColor: '#76C1A6',
          borderColor: '#76C1A6',
          // barThickness: 18,
          categoryPercentage: 0.7,
          barPercentage: 0.8,
        },
      ];
    }
  });
  return dataset;
};

const getPeriodInDays = period => {
  if (period === periods.WEEK) return 6;
  if (period === periods.MONTH) return 28;
  if (period === periods.QUARTER) return 90;
  return 365;
};

export const getStartDate = period => {
  const days = getPeriodInDays(period);

  return dayjs()
    .subtract(parseInt(days, 10), 'days')
    .format(DEFAULT_MOBILE_DATE_FORMAT);
};

export const getEndDate = () => {
  return dayjs().format(DEFAULT_MOBILE_DATE_FORMAT);
};

export const getOrCreateTooltip = chart => {
  let tooltipEl = chart.canvas.parentNode.querySelector('div');

  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.style.background = '#fff';
    tooltipEl.style.minWidth = '150px';
    tooltipEl.style.borderRadius = '2px';
    tooltipEl.style.color = '#000';
    tooltipEl.style.opacity = 1;
    tooltipEl.style.pointerEvents = 'none';
    tooltipEl.style.position = 'absolute';
    tooltipEl.style.transform = 'translate(0, -30%)';
    tooltipEl.style.transition = 'all .1s ease';
    tooltipEl.style.boxShadow = '0px 4px 8px rgba(0, 0, 0, 0.16)';

    const table = document.createElement('table');
    table.style.margin = '0px';

    tooltipEl.appendChild(table);
    chart.canvas.parentNode.appendChild(tooltipEl);
  }

  return tooltipEl;
};

export const externalTooltipHandler = (context, type) => {
  // Tooltip Element
  const { chart, tooltip } = context;
  const tooltipEl = getOrCreateTooltip(chart);

  // Hide if no tooltip
  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = 0;
    return;
  }

  // Set Text
  if (tooltip.body) {
    const titleLines = tooltip.title || [];
    const bodyLines = tooltip.body.map(b => b.lines);

    const tableHead = document.createElement('thead');

    titleLines.forEach(title => {
      const tr = document.createElement('tr');
      tr.style.borderWidth = 0;

      const th = document.createElement('th');
      th.style.borderWidth = 0;
      th.style.color = colorGrey400;
      th.style.fontWeight = 'normal';
      const text = document.createTextNode(title);

      th.appendChild(text);
      tr.appendChild(th);
      tableHead.appendChild(tr);
    });

    const getColor = (type, el) => {
      if (type === el) return '#000';
      return colorGrey400;
    };

    const getType = type => {
      if (type === 'nbu') return 'НБУ:';
      return '';
    };

    const tableBody = document.createElement('tbody');
    bodyLines.forEach((body, i) => {
      const test = body.join('').split(' ');
      const label = test.splice(0, test.length - 1).join(' ');
      const courseDisplay = test[test.length - 1];
      const nbuType = getType(type);

      const colors = tooltip.labelColors[i];
      const span = document.createElement('span');
      span.style.background = colors.borderColor;
      span.style.borderColor = colors.borderColor;
      span.style.borderRadius = '50%';
      span.style.borderWidth = '2px';
      span.style.marginRight = '8px';
      span.style.height = '8px';
      span.style.width = '8px';
      span.style.display = 'inline-block';

      const typeName = document.createTextNode(label);
      const course = document.createTextNode(courseDisplay);

      const wrapper = document.createElement('div');
      wrapper.style.display = 'flex';
      wrapper.style.justifyContent = 'space-between';
      wrapper.style.fontWeight = 'bold';
      wrapper.style.color = type === 'nbu' ? getColor(nbuType, label) : '#000';

      const div = document.createElement('div');
      div.appendChild(span);
      div.appendChild(typeName);
      div.style.marginRight = '1rem';
      div.style.fontWeight = 'normal';
      div.style.color = type === 'nbu' ? getColor(nbuType, label) : '#000';

      const tr = document.createElement('tr');
      tr.style.backgroundColor = 'inherit';
      tr.style.borderWidth = 0;

      const td = document.createElement('td');
      td.style.borderWidth = 0;

      wrapper.appendChild(div);
      wrapper.appendChild(course);
      td.appendChild(wrapper);
      tr.appendChild(td);
      tableBody.appendChild(tr);
    });

    const tableRoot = tooltipEl.querySelector('table');

    // Remove old children
    while (tableRoot.firstChild) {
      tableRoot.firstChild.remove();
    }

    // Add new children
    tableRoot.appendChild(tableHead);
    tableRoot.appendChild(tableBody);
  }

  const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

  // Display, position, and set styles for font
  if (tooltip.caretX > chart.width / 2) {
    tooltipEl.style.left = `${positionX + tooltip.caretX - 170}px`;
  } else if (tooltip.caretX < chart.width / 2) {
    tooltipEl.style.left = `${positionX + tooltip.caretX + 20}px`;
  }

  tooltipEl.style.opacity = 1;
  tooltipEl.style.fontSize = '14px';
  tooltipEl.style.top = `${positionY + tooltip.caretY}px`;
  tooltipEl.style.fontFamily = '"Roboto", sans-serif';
  // tooltipEl.style.fontWeight = 'bold';
  tooltipEl.style.padding = `${tooltip.options.padding}px ${tooltip.options.padding}px`;
};
